// `/admin/notas-fiscais` (WEB-125, `spec/28-nfse.md` §5): lista de NFS-e com filtros,
// reemitir e cancelar; aba "Configuração" (parâmetros fiscais, ambiente, liga/desliga) e
// aba "Certificado" (e-CNPJ A1, validade e envio do `.pfx`).
//
// A aba e os filtros vivem na query string (`spec/17` §3). Reemitir, cancelar e enviar o
// certificado são `ADMIN_STEP_UP_ROUTES`: o `StepUpDialog` sobe de `api/client.ts`.

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { z } from 'zod';
import { ConfirmDialog } from '../components/app/confirm-dialog.tsx';
import { PageActions } from '../components/app/shell/page-slot.tsx';
import { Button } from '../components/ui/button.tsx';
import { Input } from '../components/ui/input.tsx';
import { Label } from '../components/ui/label.tsx';
import { Segmented } from '../components/ui/segmented.tsx';
import { CertificatePanel } from '../features/admin-nfse/certificate-panel.tsx';
import { STATUSES, TAB_LABEL, TABS } from '../features/admin-nfse/model.ts';
import { NfseListPanel } from '../features/admin-nfse/nfse-list.tsx';
import { adminNfseKeys, runNfse } from '../features/admin-nfse/queries.ts';
import { SettingsPanel } from '../features/admin-nfse/settings-panel.tsx';
import { isCompetence } from '../features/nfse/model.ts';

const searchSchema = z.object({
  tab: z.enum(TABS).default('notas').catch('notas'),
  status: z.enum(STATUSES).optional().catch(undefined),
  competence: z
    .string()
    .regex(/^\d{4}-(0[1-9]|1[0-2])$/)
    .optional()
    .catch(undefined),
  seller_id: z.string().optional().catch(undefined),
});

function AdminNotasFiscais() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const search = Route.useSearch();

  const [runOpen, setRunOpen] = useState(false);
  const [runCompetence, setRunCompetence] = useState('');
  const [runDone, setRunDone] = useState<string | undefined>(undefined);

  const run = useMutation({
    mutationFn: (competence: string) => runNfse(competence),
    onSuccess: async (_, competence) => {
      setRunOpen(false);
      setRunDone(competence);
      await queryClient.invalidateQueries({ queryKey: adminNfseKeys.all });
    },
  });

  return (
    <>
      <PageActions>
        {search.tab === 'notas' && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              run.reset();
              setRunDone(undefined);
              setRunOpen(true);
            }}
          >
            Rodar competência
          </Button>
        )}
      </PageActions>

      <div className="overflow-x-auto">
        <Segmented
          aria-label="Seções de notas fiscais"
          value={search.tab}
          options={TABS.map((tab) => [tab, TAB_LABEL[tab]] as const)}
          onChange={(tab) => {
            void navigate({ to: '/admin/notas-fiscais', search: { tab } });
          }}
        />
      </div>

      {runDone !== undefined && search.tab === 'notas' && (
        <p role="status" className="text-sm text-muted-foreground">
          Emissão da competência {runDone.slice(5)}/{runDone.slice(0, 4)} enfileirada. As notas
          aparecem aqui quando o job terminar.
        </p>
      )}

      {search.tab === 'notas' && (
        <NfseListPanel
          filters={{
            ...(search.status === undefined ? {} : { status: search.status }),
            ...(search.competence === undefined ? {} : { competence: search.competence }),
            ...(search.seller_id === undefined ? {} : { seller_id: search.seller_id }),
          }}
          onFiltersChange={(next) => {
            void navigate({
              to: '/admin/notas-fiscais',
              search: {
                tab: 'notas',
                ...(next.status === undefined ? {} : { status: next.status }),
                ...(next.competence === undefined ? {} : { competence: next.competence }),
                ...(next.seller_id === undefined ? {} : { seller_id: next.seller_id }),
              },
              replace: true,
            });
          }}
        />
      )}
      {search.tab === 'configuracao' && <SettingsPanel />}
      {search.tab === 'certificado' && <CertificatePanel />}

      <ConfirmDialog
        open={runOpen}
        title="Rodar competência"
        description="Dispara o job de emissão para o mês escolhido. É idempotente: rodar de novo não cria nota duplicada. A API recusa mês ainda aberto, anterior à primeira competência ou com a emissão desligada."
        confirmLabel="Rodar"
        isPending={run.isPending}
        disabled={!isCompetence(runCompetence)}
        {...(run.error === null ? {} : { error: run.error })}
        onConfirm={() => {
          if (isCompetence(runCompetence)) {
            run.mutate(runCompetence);
          }
        }}
        onOpenChange={setRunOpen}
      >
        <div className="grid gap-2">
          <Label htmlFor="nfse-run-competence">Competência</Label>
          <Input
            id="nfse-run-competence"
            placeholder="AAAA-MM"
            maxLength={7}
            value={runCompetence}
            onChange={(event) => {
              setRunCompetence(event.target.value);
            }}
          />
        </div>
      </ConfirmDialog>
    </>
  );
}

export const Route = createFileRoute('/admin/notas-fiscais')({
  validateSearch: searchSchema,
  component: AdminNotasFiscais,
});
